/**
 * The switch review (docs/tally/09): the book's accounts, each beside the
 * TallyPrime ledger it should become, before anything changes. Feature
 * branch only.
 *
 * The book's accounts carry their history — follow-ups, notes, promises and
 * their outcomes, collector, rank, contacts, cheques, reminders — and keep
 * all of it: switching binds each account to its ledger, it does not make new
 * accounts for old customers. So nothing is bound on a guess:
 *
 *   strong     (the same GSTIN, the same name and balance, or already
 *              linked) — ticked, and can be unticked
 *   suggested  (the same name with a different balance, the same phone or
 *              email) — shown, never ticked for the owner
 *   conflict   (a GSTIN or name shared, or one name with two GSTINs) —
 *              listed, never bound; the owner matches by hand or leaves it
 *   unmatched  every account with a balance that is not bound needs a
 *              decision: match it to a ledger by hand, or confirm it settles
 *              (it is not in TallyPrime, so TallyPrime says it owes nothing)
 *
 * The database checks the same rules again (connector_activate).
 */
import { useId, useMemo, useState } from 'react';
import { Badge, Button, Card, cx, fieldClass } from '@timely/ui';
import { proposeSourceSwitch, type ProposedBinding, type SwitchExisting, type SwitchIncoming } from '@timely/domain';
import { useFormatters } from '../../book/useFormatters';
import type { ConnectorStatus } from '../useConnector';

const BASIS: Record<ProposedBinding['basis'], string> = {
    linked: 'Already linked',
    gstin: 'Same GSTIN',
    name_and_amount: 'Same name and balance',
    name_only: 'Same name, different balance',
    contact: 'Same phone or email',
};
/** Up to this many TallyPrime ledgers, a hand match is picked from a list in its row; above it, typed by name. */
const PICK_FROM_LIST = 150;
/** Rows shown before "Show all". */
const FIRST_ROWS = 100;

export function SwitchReview({
    status, existing, sheetConnected, busy, onConnect, onBack,
}: {
    status: ConnectorStatus;
    existing: SwitchExisting[];
    sheetConnected: boolean;
    busy: boolean;
    onConnect: (bindings: { account: string; sourceKey: string }[], settle: string[]) => void;
    onBack: () => void;
}) {
    const { money, count } = useFormatters();
    const incoming = useMemo<SwitchIncoming[]>(() => (status.preview?.accounts ?? []).map(a => ({
        sourceKey: a.sourceKey, company: a.name, total: a.total, totalType: a.type,
        contact: { ...(a.gstin ? { taxId: a.gstin } : {}), ...(a.phone ? { contactNumber: a.phone } : {}), ...(a.email ? { email: a.email } : {}) },
    })), [status.preview]);
    const proposal = useMemo(() => proposeSourceSwitch(existing, incoming), [existing, incoming]);
    const ledgerByKey = useMemo(() => new Map(incoming.map(l => [l.sourceKey!, l])), [incoming]);
    const ledgerByName = useMemo(() => new Map(incoming.map(l => [l.company.trim().toLowerCase(), l])), [incoming]);
    const accountById = useMemo(() => new Map(existing.map(a => [a.id, a])), [existing]);
    const existingKeys = useMemo(() => new Set(existing.map(a => a.sourceKey).filter((k): k is string => Boolean(k))), [existing]);
    // Suggestions first: they are the ones to look at.
    const ordered = useMemo(() => [...proposal.bindings].sort((x, y) => Number(x.strength === 'strong') - Number(y.strength === 'strong')), [proposal]);
    // A large book's hand match is typed against one shared list, not picked from a copy of every ledger in every row.
    const typeToMatch = incoming.length > PICK_FROM_LIST;
    const listId = useId();

    const [ticked, setTicked] = useState<Set<string>>(() => new Set(proposal.bindings.filter(b => b.strength === 'strong').map(b => b.accountId)));
    const [manual, setManual] = useState<Record<string, string>>({});   // account → ledger key, or 'settle' (picked from a list)
    const [typed, setTyped] = useState<Record<string, string>>({});     // account → a ledger's name (typed)
    const [reviewedSettling, setReviewedSettling] = useState(false);
    const [allMatches, setAllMatches] = useState(false);
    const [allDecisions, setAllDecisions] = useState(false);

    // The owner's own matches: a ledger key, 'settle', or 'unknown' (a typed name no ledger has).
    const hand: Record<string, string> = typeToMatch
        ? Object.fromEntries(Object.entries(typed).map(([id, text]) => [id, text.trim() ? (ledgerByName.get(text.trim().toLowerCase())?.sourceKey ?? 'unknown') : 'settle']))
        : manual;

    // What is bound: the ticked proposals, then the owner's own matches — never one ledger twice.
    const bound = new Map<string, string>();
    for (const b of proposal.bindings) if (ticked.has(b.accountId)) bound.set(b.accountId, b.sourceKey);
    const byProposal = new Set(bound.keys());
    const usedKeys = new Set(bound.values());
    const problems = new Map<string, string>();
    for (const [account, choice] of Object.entries(hand)) {
        if (choice === 'settle' || bound.has(account)) continue;
        if (choice === 'unknown') problems.set(account, 'No TallyPrime ledger has exactly this name.');
        else if (usedKeys.has(choice)) problems.set(account, 'That ledger is already matched to another account.');
        else { bound.set(account, choice); usedKeys.add(choice); }
    }

    // Every account with a balance that no ticked proposal binds, and that is not already carrying a key that was read, needs a decision.
    const owes = (a: SwitchExisting) => Math.abs(a.total) > 0.005;
    const deciding = existing.filter(a => owes(a) && !byProposal.has(a.id) && !(a.sourceKey && ledgerByKey.has(a.sourceKey)));
    const settling = deciding.filter(a => (hand[a.id] ?? 'settle') === 'settle');
    const newLedgers = incoming.filter(l => !usedKeys.has(l.sourceKey!) && !existingKeys.has(l.sourceKey!));
    const available = incoming.filter(l => !usedKeys.has(l.sourceKey!));
    const conflictNames = (ids: string[], keys: string[]) =>
        `${ids.map(id => accountById.get(id)?.company).filter(Boolean).join(', ')} ↔ ${keys.map(k => ledgerByKey.get(k)?.company).filter(Boolean).join(', ')}`;

    const toggle = (account: string) => setTicked(prev => { const next = new Set(prev); if (next.has(account)) next.delete(account); else next.add(account); return next; });
    const balance = (total: number, type?: 'Dr' | 'Cr') => money.balance(Math.abs(total), type ?? 'Dr');
    const strong = proposal.bindings.filter(b => b.strength === 'strong').length;
    const canConnect = problems.size === 0 && (settling.length === 0 || reviewedSettling);
    const shownMatches = allMatches ? ordered : ordered.slice(0, FIRST_ROWS);
    const shownDecisions = allDecisions ? deciding : deciding.slice(0, FIRST_ROWS);

    return (
        <Card className="p-5 sm:p-6">
            <h3 className="text-heading font-semibold text-label">5 · Review how your accounts match TallyPrime</h3>
            <p className="mt-2 text-body text-label-2 max-w-3xl">
                Your book has {count(existing.length)} account{existing.length === 1 ? '' : 's'} with their history. Matching each to its TallyPrime ledger keeps every
                follow-up, note, promise, collector, rank, contact and cheque with the right customer. Nothing changes until you connect.
            </p>
            <ul className="mt-4 flex flex-wrap gap-2" aria-label="Summary">
                <li><Badge tone="pos">{count(strong)} strong</Badge></li>
                <li><Badge tone="brand">{count(proposal.bindings.length - strong)} suggested</Badge></li>
                <li><Badge tone={proposal.conflicts.length ? 'dang' : 'neutral'}>{count(proposal.conflicts.length)} conflict{proposal.conflicts.length === 1 ? '' : 's'}</Badge></li>
                <li><Badge tone={deciding.length ? 'warn' : 'neutral'}>{count(deciding.length)} need a decision</Badge></li>
                <li><Badge>{count(newLedgers.length)} new from TallyPrime</Badge></li>
            </ul>

            {proposal.bindings.length > 0 && (
                <div className="mt-5 overflow-x-auto">
                    <table className="w-full text-body min-w-[40rem]" aria-label="Proposed matches">
                        <thead>
                            <tr className="text-left text-caption text-label-3">
                                <th scope="col" className="py-2 pr-3 font-medium">Match</th>
                                <th scope="col" className="py-2 pr-3 font-medium">Timely account</th>
                                <th scope="col" className="py-2 pr-3 font-medium">TallyPrime ledger</th>
                                <th scope="col" className="py-2 font-medium">Why</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shownMatches.map(b => {
                                const a = accountById.get(b.accountId)!;
                                const l = ledgerByKey.get(b.sourceKey)!;
                                const id = `match-${b.accountId}`;
                                return (
                                    <tr key={b.accountId} className="border-t border-separator align-top">
                                        <td className="py-2.5 pr-3">
                                            <input id={id} type="checkbox" checked={ticked.has(b.accountId)} disabled={b.basis === 'linked'}
                                                onChange={() => toggle(b.accountId)} aria-label={`Match ${a.company} to ${l.company}`} />
                                        </td>
                                        <td className="py-2.5 pr-3"><label htmlFor={id} className="font-semibold text-label break-words">{a.company}</label><div className="text-caption text-label-3 num">{balance(a.total, a.totalType)}</div></td>
                                        <td className="py-2.5 pr-3"><div className="text-label break-words">{l.company}</div><div className="text-caption text-label-3 num">{balance(l.total, l.totalType)}</div></td>
                                        <td className="py-2.5">
                                            <Badge tone={b.strength === 'strong' ? 'pos' : 'brand'}>{b.strength === 'strong' ? 'Strong' : 'Suggested'}</Badge>
                                            <span className="ml-2 text-secondary text-label-2">{BASIS[b.basis]}</span>
                                            {b.notes.slice(1).map(n => <div key={n} className="text-caption text-label-3 mt-0.5">{n}</div>)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {ordered.length > shownMatches.length && (
                        <Button variant="secondary" className="mt-3" onClick={() => setAllMatches(true)}>
                            Show all {count(ordered.length)} matches ({count(ordered.length - shownMatches.length)} more)
                        </Button>
                    )}
                </div>
            )}

            {proposal.conflicts.length > 0 && (
                <div className="mt-6">
                    <h4 className="text-body font-semibold text-label">Conflicts — not matched automatically</h4>
                    <ul className="mt-2 space-y-1.5">
                        {proposal.conflicts.map((c, i) => (
                            <li key={i} className="text-secondary text-label-2 rounded-control bg-dang-bg px-3 py-2">
                                <span className="text-dang font-semibold">{c.detail}</span> <span className="text-label-3">({conflictNames(c.accountIds, c.sourceKeys)})</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-2 text-caption text-label-3">Match these by hand below, if you know which ledger is which.</p>
                </div>
            )}

            {deciding.length > 0 && (
                <div className="mt-6">
                    <h4 className="text-body font-semibold text-label">Accounts that need a decision</h4>
                    <p className="mt-1 text-secondary text-label-3">
                        Not matched to a TallyPrime ledger. Match one by hand{typeToMatch ? ' (type the ledger’s name exactly)' : ''}, or leave it to settle:
                        TallyPrime has no balance for it, so it will be marked as settled. Its history is kept.
                    </p>
                    {typeToMatch && (
                        <datalist id={listId}>
                            {available.map(l => <option key={l.sourceKey} value={l.company}>{balance(l.total, l.totalType)}</option>)}
                        </datalist>
                    )}
                    <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-body min-w-[36rem]" aria-label="Accounts that need a decision">
                            <thead>
                                <tr className="text-left text-caption text-label-3">
                                    <th scope="col" className="py-2 pr-3 font-medium">Timely account</th>
                                    <th scope="col" className="py-2 font-medium">Decision</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shownDecisions.map(a => {
                                    const choice = manual[a.id] ?? 'settle';
                                    const problem = problems.get(a.id);
                                    const problemId = `decision-problem-${a.id}`;
                                    return (
                                        <tr key={a.id} className="border-t border-separator align-top">
                                            <td className="py-2.5 pr-3"><div className="font-semibold text-label break-words">{a.company}</div><div className="text-caption text-label-3 num">{balance(a.total, a.totalType)}</div></td>
                                            <td className="py-2.5">
                                                {typeToMatch ? (
                                                    <>
                                                        <input type="text" list={listId} aria-label={`Decision for ${a.company}`} value={typed[a.id] ?? ''}
                                                            placeholder="Settles — or type a TallyPrime ledger’s name"
                                                            aria-invalid={problem ? true : undefined} aria-describedby={problem ? problemId : undefined}
                                                            onChange={e => setTyped(prev => ({ ...prev, [a.id]: e.target.value }))}
                                                            className={fieldClass({ invalid: Boolean(problem), className: 'w-full max-w-sm' })} />
                                                        <div className="mt-1 text-caption text-label-3">
                                                            {(hand[a.id] ?? 'settle') === 'settle' ? 'Settles — not in TallyPrime' : !problem ? `Matched to ${ledgerByKey.get(hand[a.id]!)?.company}` : null}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <select aria-label={`Decision for ${a.company}`} value={choice}
                                                        aria-invalid={problem ? true : undefined} aria-describedby={problem ? problemId : undefined}
                                                        onChange={e => setManual(prev => ({ ...prev, [a.id]: e.target.value }))}
                                                        className={fieldClass({ invalid: Boolean(problem), className: 'w-full max-w-sm' })}>
                                                        <option value="settle">Settles — not in TallyPrime</option>
                                                        {incoming.filter(l => !usedKeys.has(l.sourceKey!) || l.sourceKey === choice).map(l => (
                                                            <option key={l.sourceKey} value={l.sourceKey!}>Match to {l.company} ({balance(l.total, l.totalType)})</option>
                                                        ))}
                                                    </select>
                                                )}
                                                {problem && <p id={problemId} className="mt-1 text-caption text-dang">{problem}</p>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {deciding.length > shownDecisions.length && (
                        <Button variant="secondary" className="mt-3" onClick={() => setAllDecisions(true)}>
                            Show all {count(deciding.length)} accounts ({count(deciding.length - shownDecisions.length)} more)
                        </Button>
                    )}
                </div>
            )}

            {newLedgers.length > 0 && (
                <details className="mt-6">
                    <summary className="text-body font-semibold text-label cursor-pointer">{count(newLedgers.length)} TallyPrime ledger{newLedgers.length === 1 ? '' : 's'} will become new accounts</summary>
                    <ul className="mt-2 grid gap-1 sm:grid-cols-2 text-secondary text-label-2">
                        {newLedgers.slice(0, FIRST_ROWS * 5).map(l => <li key={l.sourceKey}>{l.company} <span className="text-label-3 num">{balance(l.total, l.totalType)}</span></li>)}
                    </ul>
                    {newLedgers.length > FIRST_ROWS * 5 && <p className="mt-2 text-caption text-label-3">and {count(newLedgers.length - FIRST_ROWS * 5)} more.</p>}
                </details>
            )}

            <div className="mt-6 border-t border-separator pt-5">
                {sheetConnected && <p className="text-secondary text-label-2 mb-3">Your Google Sheet stops being the source when you connect. Its settings are kept.</p>}
                {settling.length > 0 && (
                    <label className="flex items-start gap-3 text-body text-label cursor-pointer mb-4">
                        <input type="checkbox" checked={reviewedSettling} onChange={e => setReviewedSettling(e.target.checked)} className="mt-1" />
                        <span>I have reviewed the {count(settling.length)} account{settling.length === 1 ? '' : 's'} that will be marked as settled.</span>
                    </label>
                )}
                <div className="flex flex-wrap gap-3">
                    <Button variant="primary" disabled={!canConnect} busy={busy}
                        onClick={() => onConnect([...bound].map(([account, sourceKey]) => ({ account, sourceKey })), settling.map(a => a.id))}>
                        Connect TallyPrime
                    </Button>
                    <Button variant="secondary" onClick={onBack}>Back</Button>
                </div>
                <p className={cx('mt-3 text-caption text-label-3')}>
                    {count(bound.size)} matched · {count(settling.length)} settling · {count(newLedgers.length)} new. Every change is recorded in the audit log.
                </p>
            </div>
        </Card>
    );
}
